import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, X, Image as ImageIcon } from 'lucide-react';
import { adminApi } from '../../services/api';
import { Skeleton } from '../../components/ui/Skeleton';
import type { Banner } from '../../types';

const positionLabels: Record<string, string> = {
  home_hero: 'Home — Hero',
  home_sidebar: 'Home — Barra lateral',
  dashboard: 'Dashboard',
};

const storageBase = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api').replace(/\/api\/?$/, '');
const bannerImageUrl = (path?: string) => (path ? `${storageBase}/storage/${path}` : undefined);

type BannerForm = {
  title: string;
  subtitle: string;
  link: string;
  position: string;
  starts_at: string;
  ends_at: string;
  order: string;
};

const emptyForm: BannerForm = {
  title: '',
  subtitle: '',
  link: '',
  position: 'home_hero',
  starts_at: '',
  ends_at: '',
  order: '0',
};

export const AdminBanners: React.FC = () => {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<'create' | 'edit' | null>(null);
  const [editing, setEditing] = useState<Banner | null>(null);
  const [form, setForm] = useState<BannerForm>(emptyForm);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    adminApi
      .banners()
      .then((res) => setBanners(res.data.data ?? res.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setForm(emptyForm);
    setImageFile(null);
    setModal('create');
  };

  const openEdit = (banner: Banner) => {
    setEditing(banner);
    setForm({
      title: banner.title,
      subtitle: banner.subtitle || '',
      link: banner.link || '',
      position: banner.position,
      starts_at: '',
      ends_at: '',
      order: String(banner.order ?? 0),
    });
    setModal('edit');
  };

  const closeModal = () => {
    setModal(null);
    setEditing(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (modal === 'create') {
        const data = new FormData();
        data.append('title', form.title);
        if (form.subtitle) data.append('subtitle', form.subtitle);
        if (form.link) data.append('link', form.link);
        data.append('position', form.position);
        if (form.starts_at) data.append('starts_at', form.starts_at);
        if (form.ends_at) data.append('ends_at', form.ends_at);
        data.append('order', form.order || '0');
        if (imageFile) data.append('image', imageFile);
        await adminApi.createBanner(data);
      } else if (modal === 'edit' && editing) {
        await adminApi.updateBanner(editing.id, {
          title: form.title,
          subtitle: form.subtitle || undefined,
          link: form.link || undefined,
          position: form.position,
          order: Number(form.order) || 0,
        });
      }
      closeModal();
      load();
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (banner: Banner) => {
    setTogglingId(banner.id);
    try {
      await adminApi.updateBanner(banner.id, { is_active: !banner.is_active });
      setBanners((prev) => prev.map((b) => (b.id === banner.id ? { ...b, is_active: !b.is_active } : b)));
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (banner: Banner) => {
    if (!window.confirm(`¿Eliminar el banner "${banner.title}"?`)) return;
    await adminApi.deleteBanner(banner.id);
    load();
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-onSurface">Banners</h1>
          <p className="text-onSurface-mute">Gestioná los banners promocionales de la app</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors"
        >
          <Plus className="w-4 h-4" /> Nuevo banner
        </button>
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-48 rounded-2xl" />
          ))}
        </div>
      ) : banners.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {banners.map((banner) => (
            <div key={banner.id} className="bg-surface rounded-2xl border border-outlineVarWarm overflow-hidden">
              <div className="h-32 bg-surface-var flex items-center justify-center">
                {banner.image ? (
                  <img src={bannerImageUrl(banner.image)} alt={banner.title} className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-8 h-8 text-onSurface-mute" />
                )}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-onSurface truncate">{banner.title}</p>
                    {banner.subtitle && <p className="text-xs text-onSurface-mute truncate">{banner.subtitle}</p>}
                  </div>
                  <button
                    onClick={() => handleToggleActive(banner)}
                    disabled={togglingId === banner.id}
                    className={`badge shrink-0 ${banner.is_active ? 'bg-success-100 text-success-700' : 'bg-secondary-100 text-secondary-600'} disabled:opacity-50`}
                  >
                    {banner.is_active ? 'Activo' : 'Inactivo'}
                  </button>
                </div>
                <p className="text-xs text-onSurface-mute mt-2">
                  {positionLabels[banner.position] || banner.position} · Orden {banner.order}
                </p>
                <div className="flex gap-1 mt-3 justify-end">
                  <button
                    onClick={() => openEdit(banner)}
                    className="p-2 rounded-lg text-onSurface-var hover:bg-surface-var transition-colors"
                    title="Editar"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(banner)}
                    className="p-2 rounded-lg text-danger-600 hover:bg-danger-50 transition-colors"
                    title="Eliminar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-surface rounded-2xl border border-outlineVarWarm p-12 text-center text-onSurface-mute">
          No hay banners todavía
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={closeModal}>
          <div
            className="bg-surface rounded-2xl border border-outlineVarWarm w-full max-w-md p-6 max-h-[90vh] overflow-y-auto animate-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-onSurface">
                {modal === 'create' ? 'Nuevo banner' : 'Editar banner'}
              </h2>
              <button onClick={closeModal} className="p-1 rounded-lg text-onSurface-mute hover:bg-surface-var">
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="label">Título</label>
                <input
                  required
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  className="input"
                />
              </div>

              <div>
                <label className="label">Subtítulo</label>
                <input
                  value={form.subtitle}
                  onChange={(e) => setForm((f) => ({ ...f, subtitle: e.target.value }))}
                  className="input"
                />
              </div>

              {modal === 'create' && (
                <div>
                  <label className="label">Imagen</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                    className="input"
                  />
                </div>
              )}

              <div>
                <label className="label">Link</label>
                <input
                  value={form.link}
                  onChange={(e) => setForm((f) => ({ ...f, link: e.target.value }))}
                  placeholder="https://..."
                  className="input"
                />
              </div>

              <div>
                <label className="label">Posición</label>
                <select
                  value={form.position}
                  onChange={(e) => setForm((f) => ({ ...f, position: e.target.value }))}
                  className="input"
                >
                  {Object.entries(positionLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              {modal === 'create' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label">Desde</label>
                    <input
                      type="date"
                      value={form.starts_at}
                      onChange={(e) => setForm((f) => ({ ...f, starts_at: e.target.value }))}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="label">Hasta</label>
                    <input
                      type="date"
                      value={form.ends_at}
                      onChange={(e) => setForm((f) => ({ ...f, ends_at: e.target.value }))}
                      className="input"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="label">Orden</label>
                <input
                  type="number"
                  value={form.order}
                  onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))}
                  className="input"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-outlineVarWarm text-onSurface-var text-sm font-medium hover:bg-surface-var transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 disabled:opacity-50 transition-colors"
                >
                  {saving ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
